import { useState, useEffect, useCallback } from 'react';
import { getTickets, createTicket, updateTicketStatus } from '../lib/api';
import type { Ticket } from '../types';

const POLL_INTERVAL_MS = 30_000; // refresco automático cada 30 seg

export function useTickets() {
  const [tickets, setTickets]     = useState<Ticket[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTickets();
      // El backend devuelve { tickets, has_more, next_cursor } — extraer el array
      const list = Array.isArray(data) ? data : (data as { tickets: Ticket[] }).tickets ?? [];
      setTickets(list);
      setLastUpdated(new Date());
    } catch (e) {
      setError('No se pudieron cargar los tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Carga inicial
  useEffect(() => { fetch(); }, [fetch]);

  // Polling automático cada 30 segundos
  useEffect(() => {
    const id = setInterval(fetch, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetch]);

  // Escucha evento global cuando Oski/Pame crea un ticket
  useEffect(() => {
    const handler = () => fetch();
    window.addEventListener('ticket-created', handler);
    return () => window.removeEventListener('ticket-created', handler);
  }, [fetch]);

  const create = async (ticket: Partial<Ticket>): Promise<Ticket | null> => {
    try {
      const created = await createTicket(ticket);
      setTickets(prev => [created, ...prev]);
      return created;
    } catch {
      setError('No se pudo crear el ticket');
      return null;
    }
  };

  const updateStatus = async (notionId: string, status: Ticket['status']) => {
    try {
      await updateTicketStatus(notionId, status);
      setTickets(prev =>
        prev.map(t => t.notionId === notionId ? { ...t, status } : t)
      );
    } catch {
      setError('No se pudo actualizar el estado');
    }
  };

  const byStatus = (status: Ticket['status']) =>
    tickets.filter(t => t.status === status);

  return { tickets, loading, error, fetch, lastUpdated, create, updateStatus, byStatus };
}
